import { useCallback, useState, type ReactNode } from 'react';
import { Button, type ButtonProps } from '@wapharma/design-system';

type Phase = 'idle' | 'loading' | 'success' | 'error';

export type AsyncActionButtonProps = Omit<ButtonProps<'button'>, 'onClick' | 'children'> & {
  children: ReactNode;
  pendingLabel?: string;
  successLabel?: string;
  errorLabel?: string;
  /** Reset to idle after success (ms). */
  successResetMs?: number;
  /** Reset to idle after error (ms). */
  errorResetMs?: number;
  onAction: () => Promise<void>;
};

/**
 * Local feedback for primary actions: loading / success / error text stays on the button.
 */
export function AsyncActionButton({
  children,
  pendingLabel = 'En cours…',
  successLabel = 'Enregistré',
  errorLabel = 'Échec',
  successResetMs = 2200,
  errorResetMs = 3200,
  onAction,
  disabled,
  type = 'button',
  ...rest
}: AsyncActionButtonProps) {
  const [phase, setPhase] = useState<Phase>('idle');

  const run = useCallback(async () => {
    if (phase === 'loading') return;
    setPhase('loading');
    try {
      await onAction();
      setPhase('success');
      window.setTimeout(() => setPhase('idle'), successResetMs);
    } catch {
      setPhase('error');
      window.setTimeout(() => setPhase('idle'), errorResetMs);
    }
  }, [onAction, phase, successResetMs, errorResetMs]);

  const label =
    phase === 'loading' ? pendingLabel : phase === 'success' ? successLabel : phase === 'error' ? errorLabel : children;

  return (
    <Button {...rest} type={type} disabled={disabled || phase === 'loading'} onClick={run} aria-busy={phase === 'loading'}>
      {label}
    </Button>
  );
}
